import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { toast } from 'react-toastify';
import axiosInstance from '../../apiServices/axios';
import {
    PROJECT_EMAIL_RETRY,
    PROJECT_EMAILS,
    PROJECT_FILES,
    PROJECT_RECORDING,
    PROJECT_RECORDINGS,
    PROJECT_VDR_STATUS,
    AI_WARDEN_DOCS_LIGHTWEIGHT,
} from '../../apiServices/api_routes';
import { uploadProjectFile } from '../NewProjectFlow/projectSetupApi';

import { DeleteBulkFiles, GetIRLDoc, GetIRLDocData } from '../../redux/documentsSlice';
import { downloadVdrDocumentFile } from '../../utils/vdrDocumentDownloadUtils';
import { getFileExtensionFromFile } from '../../utils/filePreviewUtils';
import {
    GetDataroomItemInfo,
    GetFolderDefaults,
    GetSearchByPost,
    GetWorkstreams,
    GetWorkstreamsDefaults,
    BulkFileDownload,
} from '../../redux/dataroomSlice';
import { GetAllGroups, GetGroupMemberPermissions, GetProjectUsers } from '../../redux/teamSlice';
import { GetInformationRequests } from '../../redux/questionBankSlice';
import { GetObservationsByScope } from '../../redux/meetingSlice';
import useVdrIndexStatusPolling from '../../hooks/useVdrIndexStatusPolling';
import VdrFileViewerModal from './VdrFileViewerModal';
import UploadProgressTray from '../Dataroom2.0/UploadProgressTray';
import VDRAnalyticsView from './views/VDRAnalyticsView';

import styles from './css/vdr.module.css';
import { mailSearchHits, observationsForRecording, recordingSearchHits, relatedForDocuments } from './liveData';

import VDRHeader from './components/VDRHeader';
import VDRSidebar from './components/VDRSidebar';
import SelectionBar from './components/SelectionBar';

import ExternalVDRModal from './modals/ExternalVDRModal';
import AddWorkstreamModal from './modals/AddWorkstreamModal';
import CreateFolderModal from './modals/CreateFolderModal';
import CreateFileModal from './modals/CreateFileModal';
import UploadFilesModal from './modals/UploadFilesModal';
import DeleteConfirmModal from './modals/DeleteConfirmModal';
import UploadRecordingModal from './modals/UploadRecordingModal';
import { ObservationFormSidebar } from '../Observations/index';

import WorkstreamView from './views/WorkstreamView';
import FolderView from './views/FolderView';
import InitialDocsView from './views/InitialDocsView';
import SearchView from './views/SearchView';
import MeetingRecordingsView from './views/MeetingRecordingsView';
import ProjectMailView from './views/ProjectMailView';

import FileDetailsPanel from './panels/FileDetailsPanel';
import MeetingRecordingPanel from './panels/MeetingRecordingPanel';
import MailDetailPanel from './panels/MailDetailPanel';

/**
 * New VDR shell. Workstreams, folders, files, mail, initial documents, and recordings use live APIs.
 * Version history and filing suggestions stay unmounted until those APIs exist.
 */
function formatMailTime(value) {
    if (!value) return '';
    try {
        return format(parseISO(value), 'd MMM yyyy · HH:mm');
    } catch {
        return String(value);
    }
}

function mapProjectEmail(email, informationRequests, observations) {
    const documentIds = [
        email.indexed_document_id,
        ...(email.attachments || []).map(attachment => attachment.indexed_document_id),
    ];
    const related = relatedForDocuments(documentIds, informationRequests, observations);
    return {
        id: email.id,
        subject: email.subject || 'Forwarded email',
        senderName: email.sender_name || email.sender_email || 'Unknown sender',
        senderEmail: email.sender_email || '',
        to: (email.to_addresses || []).join(', '),
        cc: email.cc_addresses || [],
        bcc: email.bcc_addresses || [],
        received: formatMailTime(email.created),
        attachments: (email.attachments || []).map(attachment => ({
            id: attachment.id,
            name: attachment.name,
            extension: attachment.extension || '',
            url: attachment.file || null,
            indexedDocumentId: attachment.indexed_document_id || null,
        })),
        status: email.status || 'processing',
        indexedAt: email.indexed_at ? formatMailTime(email.indexed_at) : null,
        indexedDocumentId: email.indexed_document_id || null,
        body: email.body_text || '',
        issueMessage: email.error_message || '',
        relatedIrl: related.irl.length,
        relatedObs: related.obs.length,
        documentIds,
    };
}

function mapRecording(row) {
    const names = Array.isArray(row.participants) ? row.participants : [];
    let meetingDate = row.meeting_date || '';
    if (meetingDate) {
        try {
            meetingDate = format(parseISO(meetingDate), 'd MMM yyyy');
        } catch {
            meetingDate = String(row.meeting_date);
        }
    }
    const meetingTime = row.meeting_time ? String(row.meeting_time).slice(0, 5) : '';
    return {
        id: row.id,
        title: row.title,
        participants: row.participant_count ?? names.length,
        participantNames: names,
        meetingDate,
        meetingTime,
        duration: row.duration || '',
        source: row.source || 'MS Teams',
        meetingLink: row.meeting_link || '',
        status: row.status || 'ready',
        transcriptStatus: Array.isArray(row.transcript) && row.transcript.length ? 'ready' : 'none',
        addedBy: row.added_by_name || '',
        recordingFile: row.recording_file || '',
        transcript: Array.isArray(row.transcript) ? row.transcript : [],
        aiSnapshot: row.ai_snapshot || null,
    };
}

function draftObservationFromRecording(recording) {
    const snapshot = recording?.aiSnapshot;
    const topics = (snapshot?.keyTopics || []).filter(Boolean);
    const info = (snapshot?.keyInformation || []).filter(Boolean);
    if (!topics.length && !info.length) {
        return recording?.title ? `<p>Observation from ${recording.title}</p>` : '';
    }
    const blocks = [];
    if (topics.length) {
        blocks.push(`<p><strong>Key topics</strong></p><ul>${topics.map(topic => `<li>${topic}</li>`).join('')}</ul>`);
    }
    if (info.length) {
        blocks.push(`<p><strong>Key information</strong></p><ul>${info.map(item => `<li>${item}</li>`).join('')}</ul>`);
    }
    return blocks.join('');
}

function dedupeSearchFiles(files) {
    const seen = new Map();
    (files || []).forEach(file => {
        const key = String(file.file_id || file.id || file.metadata_storage_name || '');
        if (!key) return;
        const prev = seen.get(key);
        if (!prev) {
            seen.set(key, file);
            return;
        }
        if (!prev.snippet && file.snippet) seen.set(key, { ...prev, snippet: file.snippet });
    });
    return [...seen.values()];
}

function mapProjectFile(file) {
    return {
        id: file.id,
        document_name: file.document_name,
        document: file.document,
        extension: file.extension || '',
        created: file.created,
        upload_date: file.created,
        uploaded_by_name: file.uploaded_by_name || '',
        upload_by: file.uploaded_by,
        indexed_document_id: file.indexed_document_id,
        read_by: file.read_by || [],
        status: file.indexed_document_id ? 'ready' : 'processing',
        indexing_status: file.indexed_document_id ? 'ready' : 'processing',
    };
}

const VDR = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    useVdrIndexStatusPolling();

    const { selectedProject } = useSelector(state => state?.projectData);
    const { projectUsersAll, groups: accessGroups } = useSelector(state => state?.team);
    const { user } = useSelector(state => state?.user);
    const { workstreams, items, searchedFiles } = useSelector(state => state?.dataroom);
    const { data, bulkFilesLoading, indexingFiles } = useSelector(state => state?.documents);
    const informationRequests = useSelector(state => state?.questionBank?.informationRequests || []);
    const scopeObservations = useSelector(state => state?.meeting?.scopeObservations || []);

    const [activeView, setActiveView] = React.useState('workstream'); // initial | mail | recordings | workstream | search | analytics
    const [analyticsContext, setAnalyticsContext] = React.useState({ type: 'project' });
    const [workstream, setWorkstream] = React.useState(null);
    const [expandedWorkstreamId, setExpandedWorkstreamId] = React.useState(null);
    const [selectedFolder, setSelectedFolder] = React.useState(null);
    const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);

    const [selectedFile, setSelectedFile] = React.useState(null);
    const [showFileModal, setShowFileModal] = React.useState(false);
    const [selectedFileIndex, setSelectedFileIndex] = React.useState(0);
    const [versionPreview, setVersionPreview] = React.useState(null);
    const [selectedRows, setSelectedRows] = React.useState([]);

    const [selectedRecording, setSelectedRecording] = React.useState(null);
    const [recordings, setRecordings] = React.useState([]);
    const [observationFormOpen, setObservationFormOpen] = React.useState(false);
    const [observationRecordingId, setObservationRecordingId] = React.useState(null);
    const [observationDraft, setObservationDraft] = React.useState('');
    const [selectedMail, setSelectedMail] = React.useState(null);
    const mailbox = selectedProject?.inbound_email || '';
    const isProjectAdmin =
        Boolean(user?.is_superuser) ||
        selectedProject?.vdr?.vdr_admin === user?.id ||
        Boolean(selectedProject?.project_admins?.some(admin => admin.user_id === user?.id));
    const memberHasFeature = field => {
        if (isProjectAdmin) return true;
        const mine = (projectUsersAll || []).filter(row => String(row?.value?.user_id) === String(user?.id));
        if (!mine.length || !accessGroups?.length) return false;
        return mine.some(row => {
            const groupId = String(row.group || '').split(':')[0];
            const group = accessGroups.find(item => String(item.id) === groupId);
            return Boolean(group?.permissions?.[field]);
        });
    };
    const canUseMail = memberHasFeature('project_mail');
    const canUseMeetings = memberHasFeature('project_meetings');
    const [savedMails, setSavedMails] = React.useState([]);
    const [projectFiles, setProjectFiles] = React.useState([]);
    const [vdrStatus, setVdrStatus] = React.useState(null);
    const [searchParams, setSearchParams] = useSearchParams();

    const loadMails = React.useCallback(() => {
        if (!selectedProject?.id || !canUseMail) {
            setSavedMails([]);
            return Promise.resolve();
        }
        return axiosInstance
            .get(PROJECT_EMAILS(selectedProject.id))
            .then(response => {
                const rows = Array.isArray(response.data) ? response.data : [];
                setSavedMails(rows);
            })
            .catch(() => setSavedMails([]));
    }, [canUseMail, selectedProject?.id]);

    const loadProjectFiles = React.useCallback(() => {
        if (!selectedProject?.id) {
            setProjectFiles([]);
            return Promise.resolve();
        }
        return axiosInstance
            .get(PROJECT_FILES(selectedProject.id))
            .then(response => setProjectFiles(Array.isArray(response.data) ? response.data.map(mapProjectFile) : []))
            .catch(() => setProjectFiles([]));
    }, [selectedProject?.id]);

    const loadRecordings = React.useCallback(() => {
        if (!selectedProject?.id || !canUseMeetings) {
            setRecordings([]);
            return Promise.resolve();
        }
        return axiosInstance
            .get(PROJECT_RECORDINGS(selectedProject.id))
            .then(response => setRecordings(Array.isArray(response.data) ? response.data.map(mapRecording) : []))
            .catch(() => setRecordings([]));
    }, [canUseMeetings, selectedProject?.id]);

    const loadVdrStatus = React.useCallback(() => {
        if (!selectedProject?.id) {
            setVdrStatus(null);
            return Promise.resolve();
        }
        return axiosInstance
            .get(PROJECT_VDR_STATUS(selectedProject.id))
            .then(response => setVdrStatus(response.data || null))
            .catch(() => setVdrStatus(null));
    }, [selectedProject?.id]);

    React.useEffect(() => {
        loadMails();
        loadProjectFiles();
        loadRecordings();
        loadVdrStatus();
    }, [loadMails, loadProjectFiles, loadRecordings, loadVdrStatus]);

    React.useEffect(() => {
        if ((activeView === 'mail' && !canUseMail) || (activeView === 'recordings' && !canUseMeetings)) {
            setActiveView('workstream');
        }
    }, [activeView, canUseMail, canUseMeetings]);

    const projectMails = React.useMemo(
        () => savedMails.map(email => mapProjectEmail(email, informationRequests, scopeObservations)),
        [savedMails, informationRequests, scopeObservations],
    );

    const [searchQuery, setSearchQuery] = React.useState('');
    const [searchLoading, setSearchLoading] = React.useState(false);

    const [modals, setModals] = React.useState({
        externalVdr: false,
        addWorkstream: false,
        editWorkstream: false,
        createFolder: false,
        createFile: false,
        editFolder: false,
        uploadFiles: false,
        uploadFolder: false,
        reviewFiling: false,
        chooseLocation: false,
        deleteConfirm: false,
        uploadRecording: false,
    });
    const [editWorkstreamTarget, setEditWorkstreamTarget] = React.useState(null);

    const openModal = key => setModals(m => ({ ...m, [key]: true }));
    const closeModal = key => setModals(m => ({ ...m, [key]: false }));

    React.useEffect(() => {
        if (!selectedProject?.id) return;
        dispatch(GetWorkstreams());
        dispatch(GetWorkstreamsDefaults());
        dispatch(GetProjectUsers());
        dispatch(GetAllGroups());
        dispatch(GetInformationRequests());
        dispatch(GetObservationsByScope('all'));
        if (user?.role !== 'Investor') {
            dispatch(GetGroupMemberPermissions(localStorage['group_member_id']));
        }
    }, [selectedProject?.id, dispatch, user?.role]);

    React.useEffect(() => {
        if (!workstreams?.length) return;
        const stored = localStorage.getItem('workstream');
        const next = workstreams.find(w => String(w.id) === String(stored)) || workstreams[0];
        if (!workstream || !workstreams.find(w => w.id === workstream.id)) {
            setWorkstream(next);
            setExpandedWorkstreamId(next?.id);
            localStorage.setItem('workstream', next?.id);
        }
    }, [workstreams]); // eslint-disable-line react-hooks/exhaustive-deps

    const [workstreamFilesLoading, setWorkstreamFilesLoading] = React.useState(false);

    React.useEffect(() => {
        if (!workstream?.id) {
            setWorkstreamFilesLoading(false);
            return undefined;
        }
        let active = true;
        setWorkstreamFilesLoading(true);
        dispatch(GetDataroomItemInfo(workstream.id));
        dispatch(GetFolderDefaults(workstream.name));
        dispatch(GetIRLDoc(workstream.id))
            .unwrap()
            .catch(() => {})
            .finally(() => {
                if (active) setWorkstreamFilesLoading(false);
            });
        return () => {
            active = false;
        };
    }, [workstream?.id, workstream?.name, dispatch]);

    const folders = items || [];
    const [lightweightDocs, setLightweightDocs] = React.useState(null);

    React.useEffect(() => {
        const projectId = selectedProject?.id;
        if (!projectId) return undefined;
        let cancelled = false;
        axiosInstance
            .get(AI_WARDEN_DOCS_LIGHTWEIGHT(projectId))
            .then(response => {
                const docs = response.data?.documents;
                if (!cancelled) setLightweightDocs(Array.isArray(docs) ? docs : []);
            })
            .catch(() => {
                // Keep the counts already on screen if the catalog request fails.
            });
        return () => {
            cancelled = true;
        };
    }, [selectedProject?.id, data]);

    // `data` stays on the previously opened workstream until GetIRLDoc finishes.
    // Only treat it as this workstream's files once every row says so.
    const loadedDocsForWorkstream = React.useMemo(() => {
        if (!workstream?.id || workstreamFilesLoading) return null;
        if (!Array.isArray(data)) return null;
        if (!data.length) return [];
        const matches = data.every(doc => String(doc?.workstream_id) === String(workstream.id));
        return matches ? data : null;
    }, [data, workstream?.id, workstreamFilesLoading]);

    const fileCountsByFolder = React.useMemo(() => {
        const map = {};
        const add = doc => {
            const id = doc?.ws_folder_id;
            if (id == null) return;
            map[id] = (map[id] || 0) + 1;
        };
        (lightweightDocs || []).forEach(doc => {
            if (loadedDocsForWorkstream && String(doc?.workstream_id) === String(workstream?.id)) return;
            add(doc);
        });
        (loadedDocsForWorkstream || []).forEach(add);
        (folders || []).forEach(folder => {
            const parentId = folder.parent ?? folder.parent_id;
            if (parentId == null) return;
            map[parentId] = (map[parentId] || 0) + 1;
        });
        return map;
    }, [lightweightDocs, loadedDocsForWorkstream, workstream?.id, folders]);

    const fileCountsByWorkstream = React.useMemo(() => {
        const map = {};
        (workstreams || []).forEach(ws => {
            map[ws.id] = ws.document_count ?? 0;
        });
        if (!lightweightDocs) return map;
        (workstreams || []).forEach(ws => {
            map[ws.id] = 0;
        });
        lightweightDocs.forEach(doc => {
            const id = doc?.workstream_id;
            if (id == null) return;
            map[id] = (map[id] || 0) + 1;
        });
        return map;
    }, [lightweightDocs, workstreams]);

    const folderFiles = React.useMemo(() => {
        const source = loadedDocsForWorkstream ?? [];
        if (!selectedFolder) return source;
        return source.filter(doc => doc?.ws_folder_id === selectedFolder.id);
    }, [loadedDocsForWorkstream, selectedFolder]);

    const initialDocs = projectFiles;

    const statusCounts = React.useMemo(() => {
        const indexing = (indexingFiles || []).filter(item => !selectedProject?.id || item.projectId == null || String(item.projectId) === String(selectedProject.id));
        const processingQueue = indexing.filter(item => item.status === 'indexing' || item.status === 'uploading').length;
        const failedQueue = indexing.filter(item => item.status === 'failed').length;
        const documents = Number(vdrStatus?.documents) || 0;
        const processing = processingQueue + Number(vdrStatus?.emails_processing || 0) + Number(vdrStatus?.project_files_processing || 0);
        const needsAttention = failedQueue + Number(vdrStatus?.emails_needs_attention || 0) + Number(vdrStatus?.project_files_failed || 0);
        return {
            ready: Math.max(0, documents - processingQueue - failedQueue),
            processing,
            needsAttention,
        };
    }, [indexingFiles, selectedProject?.id, vdrStatus]);

    const buildAnalyticsContext = React.useCallback(() => {
        if (activeView === 'initial') {
            return { type: 'initial' };
        }
        if (selectedFolder && workstream) {
            return {
                type: 'folder',
                workstreamId: workstream.id,
                folderId: selectedFolder.id,
                workstreamName: workstream.name,
                folderName: selectedFolder.name,
            };
        }
        if (workstream) {
            return {
                type: 'workstream',
                workstreamId: workstream.id,
                workstreamName: workstream.name,
            };
        }
        return { type: 'project' };
    }, [activeView, selectedFolder, workstream]);

    const handleSelectWorkstream = ws => {
        if (workstream?.id === ws?.id && expandedWorkstreamId === ws?.id) {
            setExpandedWorkstreamId(null);
            return;
        }
        setWorkstream(ws);
        setExpandedWorkstreamId(ws?.id);
        setSelectedFolder(null);
        setSelectedFile(null);
        setSelectedRows([]);
        localStorage.setItem('workstream', ws?.id);
        if (activeView === 'analytics') {
            setAnalyticsContext({
                type: 'workstream',
                workstreamId: ws.id,
                workstreamName: ws.name,
            });
            dispatch(GetDataroomItemInfo(ws.id));
            dispatch(GetIRLDoc(ws.id));
            return;
        }
        setActiveView('workstream');
    };

    const handleSelectFolder = (ws, folder) => {
        if (ws && ws.id !== workstream?.id) {
            setWorkstream(ws);
            localStorage.setItem('workstream', ws.id);
            dispatch(GetDataroomItemInfo(ws.id));
            dispatch(GetIRLDoc(ws.id));
        }
        setExpandedWorkstreamId(ws?.id || workstream?.id);
        setSelectedFolder(folder);
        setSelectedFile(null);
        setSelectedRows([]);
        if (activeView === 'analytics') {
            setAnalyticsContext({
                type: 'folder',
                workstreamId: (ws || workstream)?.id,
                folderId: folder.id,
                workstreamName: (ws || workstream)?.name,
                folderName: folder.name,
            });
            return;
        }
        setActiveView('workstream');
    };

    const handleSelectView = view => {
        if (activeView === 'analytics' && view === 'initial') {
            setAnalyticsContext({ type: 'initial' });
            setSelectedFolder(null);
            setSelectedFile(null);
            setSelectedRecording(null);
            setSelectedMail(null);
            setSelectedRows([]);
            return;
        }
        setActiveView(view);
        setSelectedFolder(null);
        setSelectedFile(null);
        setSelectedRecording(null);
        setSelectedMail(null);
        setSelectedRows([]);
        if (view === 'search') setSearchQuery('');
    };

    const handleOpenAnalytics = () => {
        setAnalyticsContext(buildAnalyticsContext());
        setActiveView('analytics');
        setSelectedFile(null);
        setSelectedRows([]);
    };

    const handleBackFromAnalytics = () => {
        if (analyticsContext.type === 'initial') {
            setActiveView('initial');
            return;
        }
        setActiveView('workstream');
    };

    const openCreateFile = folder => {
        if (!workstream) {
            toast.info('Select a workstream first');
            return;
        }
        const target = folder || selectedFolder;
        if (!target?.id) {
            toast.info('Select a folder first');
            return;
        }
        if (folder?.id && folder.id !== selectedFolder?.id) setSelectedFolder(folder);
        if (activeView !== 'workstream') setActiveView('workstream');
        openModal('createFile');
    };

    const handleAddNew = action => {
        if (action === 'upload_files') openModal('uploadFiles');
        else if (action === 'create_file') openCreateFile();
        else if (action === 'upload_folder') openModal('uploadFolder');
        else if (action === 'create_folder') {
            if (!workstream) {
                toast.info('Select a workstream first');
                return;
            }
            openModal('createFolder');
        } else if (action === 'create_workstream') openModal('addWorkstream');
        else if (action === 'upload_recording') {
            if (!canUseMeetings) return;
            setActiveView('recordings');
            openModal('uploadRecording');
        }
    };

    const openSearchResult = result => {
        if (result?.kind === 'mail') {
            const mail = projectMails.find(item => item.id === result.sourceId);
            if (!mail) return;
            setActiveView('mail');
            setSelectedMail(mail);
            setSelectedFile(null);
            setSelectedRecording(null);
            return;
        }
        if (result?.kind === 'recording') {
            const recording = recordings.find(item => item.id === result.sourceId);
            if (!recording) return;
            setActiveView('recordings');
            setSelectedRecording(recording);
            setSelectedMail(null);
            setSelectedFile(null);
            return;
        }
        const fileId = result?.file_id || result?.id;
        const file = (data || []).find(item => String(item.id) === String(fileId));
        const opened = {
            ...(file || {}),
            id: file?.id || fileId,
            document_name: result.document_name || result.displayName || file?.document_name || result.metadata_storage_name,
            document: file?.document || result.document,
            document_link: file?.document_link || result.document_link,
            workstream_id: result.workstream_id || file?.workstream_id,
            ws_folder_id: result.ws_folder_id || file?.ws_folder_id,
        };
        if (opened.workstream_id || result.workstream_name) {
            opened.searchWorkstream = {
                id: opened.workstream_id,
                name: result.workstream_name || result.workstreamName || file?.workstream || '',
            };
        }
        if (opened.ws_folder_id || result.ws_folder) {
            opened.searchFolder = {
                id: opened.ws_folder_id,
                name: result.ws_folder || file?.ws_folder || '',
            };
        }
        handleOpenFileInfo(opened);
    };

    const handleSearchSubmit = async (text = searchQuery) => {
        const anchorId = workstream?.id || workstreams?.[0]?.id;
        if (!anchorId || !text?.trim()) return;
        setSearchLoading(true);
        setActiveView('search');
        try {
            const fd = new FormData();
            fd.append('scope', 'project');
            await dispatch(
                GetSearchByPost({
                    workstream: anchorId,
                    searchText: text.trim(),
                    formData: fd,
                }),
            );
        } finally {
            setSearchLoading(false);
        }
    };

    const enrichFileForViewer = file => {
        if (!file) return file;
        const ext = getFileExtensionFromFile(file);
        const workstreamId =
            file.workstream_id && file.workstream_id !== 'initial' ? file.workstream_id : workstream?.id;
        return {
            ...file,
            ...(ext ? { extension: file.extension || ext } : {}),
            ...(workstreamId && !file.workstream_id ? { workstream_id: workstreamId } : {}),
        };
    };

    const handleOpenFile = async file => {
        const opened = enrichFileForViewer(file);
        if (file?.versionPreview) {
            setVersionPreview(opened);
            setShowFileModal(true);
            return;
        }
        setVersionPreview(null);
        setSelectedFile(opened);
        const nextIndex = folderFiles.findIndex(row => String(row.id) === String(opened.id));
        setSelectedFileIndex(nextIndex >= 0 ? nextIndex : 0);
        setShowFileModal(true);
        const wsId = file?.workstream_id && file.workstream_id !== 'initial' ? file.workstream_id : workstream?.id;
        if (file?.id && wsId) {
            dispatch(GetIRLDocData({ id: file.id, workstream: wsId }));
        }
    };

    const handleOpenFileInfo = file => {
        setSelectedFile(file);
        setSelectedRecording(null);
        setSelectedMail(null);
        const wsId = file?.workstream_id && file.workstream_id !== 'initial' ? file.workstream_id : workstream?.id;
        if (file?.id && wsId) {
            dispatch(GetIRLDocData({ id: file.id, workstream: wsId }));
        }
    };

    const selectedFileForPanel = React.useMemo(() => {
        if (!selectedFile?.id) return null;
        const fresh = (data || []).find(doc => String(doc.id) === String(selectedFile.id));
        return fresh ? { ...selectedFile, ...fresh } : selectedFile;
    }, [selectedFile, data]);

    React.useEffect(() => {
        const mailId = searchParams.get('mail');
        if (!mailId || !projectMails.length) return;
        const match = projectMails.find(mail => String(mail.id) === String(mailId));
        if (!match) return;
        setActiveView('mail');
        setSelectedMail(match);
        setSelectedFile(null);
        setSelectedRecording(null);
    }, [searchParams, projectMails]);

    React.useEffect(() => {
        const recordingId = searchParams.get('recording');
        if (!recordingId || !recordings.length) return;
        const match = recordings.find(row => String(row.id) === String(recordingId));
        if (!match) return;
        setActiveView('recordings');
        setSelectedRecording(match);
        setSelectedFile(null);
        setSelectedMail(null);
    }, [searchParams, recordings]);

    React.useEffect(() => {
        const initialId = searchParams.get('initial');
        if (!initialId || !projectFiles.length) return;
        const match = projectFiles.find(file => String(file.id) === String(initialId));
        if (!match) return;
        setActiveView('initial');
        setSelectedFile(match);
        setSelectedMail(null);
        setSelectedRecording(null);
    }, [searchParams, projectFiles]);

    React.useEffect(() => {
        const recording = selectedRecording;
        if (!selectedProject?.id || !recording?.id) return undefined;
        if (recording.aiSnapshot) return undefined;
        const hasTranscript = Array.isArray(recording.transcript) && recording.transcript.length > 0;
        if (!hasTranscript) return undefined;
        let cancelled = false;
        const markHydrated = mapped => ({ ...mapped, snapshotHydrationAttempted: true });
        axiosInstance
            .get(PROJECT_RECORDING(selectedProject.id, recording.id))
            .then(response => {
                if (cancelled) return;
                const mapped = markHydrated(mapRecording(response.data));
                setSelectedRecording(current => (current?.id === mapped.id ? mapped : current));
                setRecordings(prev => prev.map(row => (row.id === mapped.id ? mapped : row)));
            })
            .catch(() => {
                if (cancelled) return;
                setSelectedRecording(current =>
                    current?.id === recording.id ? { ...current, snapshotHydrationAttempted: true } : current,
                );
            });
        return () => {
            cancelled = true;
        };
    }, [selectedProject?.id, selectedRecording?.id, selectedRecording?.aiSnapshot, selectedRecording?.transcript?.length]);

    React.useEffect(() => {
        setSelectedMail(current => {
            if (!current) return current;
            const next = projectMails.find(mail => mail.id === current.id);
            if (!next) return null;
            if (
                next.status === current.status &&
                next.issueMessage === current.issueMessage &&
                next.relatedIrl === current.relatedIrl &&
                next.relatedObs === current.relatedObs &&
                next.body === current.body
            ) {
                return current;
            }
            return next;
        });
    }, [projectMails]);

    const copyResourceLink = async (param, id) => {
        const link = `${window.location.origin}/dataroom?${param}=${id}`;
        try {
            await navigator.clipboard.writeText(link);
            toast.success('Link copied');
        } catch {
            toast.info(link);
        }
    };

    const openStoredFile = async (fileLike) => {
        const url = fileLike?.document || fileLike?.url;
        if (!url) {
            toast.info('This file cannot be opened');
            return;
        }
        const opened = enrichFileForViewer({
            id: fileLike.id,
            document_name: fileLike.document_name || fileLike.name,
            document: url,
            extension: fileLike.extension,
            source: fileLike.source,
            emailId: fileLike.emailId,
            indexed_document_id: fileLike.indexed_document_id,
            can_download_original: fileLike.can_download_original,
        });
        setSelectedFile(opened);
        setSelectedFileIndex(0);
        setShowFileModal(true);
    };

    const mailAttachmentFile = attachment => ({
        id: attachment.id,
        name: attachment.name,
        document_name: attachment.name,
        extension: attachment.extension,
        url: attachment.url,
        source: 'project_mail_attachment',
        emailId: selectedMail?.id,
    });

    const downloadStoredFile = async (fileLike) => {
        const ok = await downloadVdrDocumentFile(dispatch, fileLike, {
            workstream,
            isInitialProjectFile: activeView === 'initial',
        });
        if (!ok) {
            toast.error('Download failed');
        }
    };

    const handleDownloadFile = file => {
        const isProjectAdmin =
            selectedProject?.vdr?.vdr_admin === user?.id ||
            selectedProject?.project_admins?.some(admin => admin.user_id === user?.id);
        if (!(file?.can_download_original || isProjectAdmin)) {
            toast.info('You do not have permission to download this file');
            return;
        }
        downloadStoredFile(file);
    };

    const selectedFileRelated = React.useMemo(() => {
        if (!selectedFile?.id) return { irl: [], obs: [] };
        const documentId = selectedFile.indexed_document_id || selectedFile.id;
        return relatedForDocuments([documentId], informationRequests, scopeObservations);
    }, [selectedFile?.id, selectedFile?.indexed_document_id, informationRequests, scopeObservations]);

    const deleteLinked = React.useMemo(() => {
        const related = relatedForDocuments(selectedRows, informationRequests, scopeObservations);
        return { informationRequests: related.irl.length, observations: related.obs.length };
    }, [selectedRows, informationRequests, scopeObservations]);

    const handleOpenRelated = (mode, item) => {
        if (mode === 'related_irl') navigate(`/irl?irlId=${item.id}`);
        else navigate(`/observations?observationId=${item.id}`);
    };

    const handleRetryMail = async mail => {
        if (!selectedProject?.id || !mail?.id) return;
        try {
            await axiosInstance.post(PROJECT_EMAIL_RETRY(selectedProject.id, mail.id));
            toast.success('Email queued for indexing');
        } catch (error) {
            const message = error?.response?.data?.error_message;
            if (message) toast.error(message);
        }
        await loadMails();
        await loadVdrStatus();
    };

    const handleUploadInitial = async files => {
        if (!selectedProject?.id) return;
        try {
            for (const file of files) {
                await uploadProjectFile(selectedProject.id, file);
            }
            toast.success('Upload started');
            await loadProjectFiles();
            await loadVdrStatus();
        } catch (error) {
            const detail = error?.response?.data?.detail;
            toast.error(detail || 'Upload failed');
        }
    };

    const handleUploadRecording = async body => {
        const response = await axiosInstance.post(PROJECT_RECORDINGS(selectedProject.id), body);
        let mapped = mapRecording(response.data);
        const hasTranscript = Array.isArray(mapped.transcript) && mapped.transcript.length > 0;
        if (!mapped.aiSnapshot && hasTranscript) {
            try {
                const detail = await axiosInstance.get(PROJECT_RECORDING(selectedProject.id, mapped.id));
                mapped = mapRecording(detail.data);
            } catch {
                // keep create response
            }
        }
        setRecordings(prev => [mapped, ...prev.filter(row => row.id !== mapped.id)]);
        setSelectedRecording(mapped);
        setActiveView('recordings');
        toast.success('Recording uploaded');
        return mapped;
    };

    const handleDeleteRecording = async recording => {
        if (!selectedProject?.id || !recording?.id) return;
        await axiosInstance.delete(PROJECT_RECORDING(selectedProject.id, recording.id));
        setRecordings(prev => prev.filter(row => row.id !== recording.id));
        setSelectedRecording(null);
        if (searchParams.get('recording') === String(recording.id)) {
            const next = new URLSearchParams(searchParams);
            next.delete('recording');
            setSearchParams(next, { replace: true });
        }
        toast.success('Recording deleted');
    };

    const resetRecordingObservationForm = () => {
        setObservationRecordingId(null);
        setObservationDraft('');
    };

    const handleOpenRecordingObservation = recording => {
        if (!recording?.id) return;
        setObservationRecordingId(recording.id);
        setObservationDraft(draftObservationFromRecording(recording));
        setObservationFormOpen(true);
    };

    const handleDeleteSelected = async () => {
        if (!selectedRows.length || !workstream?.id) return;
        await dispatch(DeleteBulkFiles({ ws_id: workstream.id, items: selectedRows }));
        setSelectedRows([]);
        closeModal('deleteConfirm');
        toast.success('Files deleted');
    };

    const handleDownloadSelected = async () => {
        if (!selectedRows.length || !workstream?.id) return;
        const formData = new FormData();
        formData.append('file_ids', JSON.stringify(selectedRows));
        try {
            const response = await dispatch(BulkFileDownload({ id: workstream.id, body: formData }));
            const payload = response?.payload;
            if (!payload?.data) {
                toast.error('Download failed');
                return;
            }
            const blob = new Blob([payload.data], { type: 'application/zip' });
            let filename = `${workstream.name || 'Files'}.zip`;
            const contentDisposition = payload.headers?.['content-disposition'];
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
                if (filenameMatch?.[1]) {
                    filename = filenameMatch[1];
                }
            }
            const url = window.URL.createObjectURL(blob);
            const downloadLink = document.createElement('a');
            downloadLink.href = url;
            downloadLink.download = filename;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            downloadLink.remove();
            window.URL.revokeObjectURL(url);
            const requested = Number(payload.headers?.['x-lens-zip-requested']);
            const included = Number(payload.headers?.['x-lens-zip-included']);
            if (
                Number.isFinite(requested) &&
                Number.isFinite(included) &&
                included < requested
            ) {
                toast.warning(
                    `Downloaded ${included} of ${requested} files. Some files may be missing from storage or you may not have download permission.`,
                );
            }
        } catch (error) {
            console.error('Bulk download failed:', error);
            toast.error('Download failed');
        }
    };

    const renderMain = () => {
        if (activeView === 'analytics') {
            return (
                <div className={styles.mainScroll}>
                    <VDRAnalyticsView
                        workstreams={workstreams || []}
                        folders={folders}
                        projectFiles={projectFiles}
                        projectUsersAll={projectUsersAll}
                        scope={analyticsContext}
                        onOpenFile={handleOpenFile}
                    />
                </div>
            );
        }
        if (activeView === 'search') {
            const fileHits = dedupeSearchFiles(searchedFiles || []);
            const results = [
                ...mailSearchHits(searchQuery, projectMails),
                ...recordingSearchHits(searchQuery, recordings),
                ...fileHits,
            ];
            return (
                <div className={`${styles.mainScroll} ${styles.searchScroll}`}>
                    <SearchView
                        query={searchQuery}
                        setQuery={setSearchQuery}
                        results={results}
                        loading={searchLoading}
                        onSearch={handleSearchSubmit}
                        onOpenResult={openSearchResult}
                        documents={data || []}
                        workstreams={workstreams || []}
                        indexingFiles={indexingFiles}
                        projectId={selectedProject?.id}
                    />
                </div>
            );
        }
        if (activeView === 'initial') {
            return (
                <div className={styles.mainScroll}>
                    <InitialDocsView
                        files={initialDocs}
                        selectedFileId={selectedFile?.id}
                        onOpenFile={openStoredFile}
                        onOpenFileInfo={handleOpenFileInfo}
                        onUploadFiles={handleUploadInitial}
                        projectUsersAll={projectUsersAll}
                    />
                </div>
            );
        }
        if (activeView === 'mail') {
            return (
                <div className={styles.mainScroll}>
                    <ProjectMailView
                        mails={projectMails}
                        selectedId={selectedMail?.id}
                        mailbox={mailbox}
                        onSelect={mail => {
                            setSelectedMail(mail);
                            setSelectedFile(null);
                            setSelectedRecording(null);
                        }}
                    />
                </div>
            );
        }
        if (activeView === 'recordings') {
            return (
                <div className={styles.mainScroll}>
                    <MeetingRecordingsView
                        recordings={recordings}
                        selectedId={selectedRecording?.id}
                        onSelect={rec => {
                            setSelectedRecording(rec);
                            setSelectedFile(null);
                            setSelectedMail(null);
                        }}
                        onUpload={() => openModal('uploadRecording')}
                    />
                </div>
            );
        }

        // workstream / folder
        if (selectedFolder) {
            const subfolders = (folders || []).filter(f => f.parent === selectedFolder.id || f.parent_id === selectedFolder.id);
            return (
                <div className={styles.mainScroll}>
                    <FolderView
                        workstream={workstream}
                        folder={selectedFolder}
                        subfolders={subfolders}
                        files={folderFiles}
                        selectedRows={selectedRows}
                        setSelectedRows={setSelectedRows}
                        selectedFileId={selectedFile?.id}
                        onOpenFile={handleOpenFile}
                        onOpenFileInfo={handleOpenFileInfo}
                        onOpenSubfolder={folder => setSelectedFolder(folder)}
                        onEditFolder={() => openModal('editFolder')}
                        onCreateFile={openCreateFile}
                        onUploadFiles={() => openModal('uploadFiles')}
                        projectUsersAll={projectUsersAll}
                        indexingFiles={indexingFiles}
                        selectedProject={selectedProject}
                        fileCountsByFolder={fileCountsByFolder}
                        filesLoading={workstreamFilesLoading}
                    />
                    {selectedRows.length > 0 && (
                        <SelectionBar
                            count={selectedRows.length}
                            onClear={() => setSelectedRows([])}
                            onDelete={() => openModal('deleteConfirm')}
                            onDownload={handleDownloadSelected}
                        />
                    )}
                </div>
            );
        }

        return (
            <div className={styles.mainScroll}>
                <WorkstreamView
                    workstream={workstream}
                    folders={folders}
                    fileCountsByFolder={fileCountsByFolder}
                    onOpenFolder={folder => setSelectedFolder(folder)}
                    onEditWorkstream={() => {
                        setEditWorkstreamTarget(workstream);
                        openModal('editWorkstream');
                    }}
                    filingCount={0}
                />
            </div>
        );
    };

    return (
        <div className={styles.page}>
            <VDRHeader
                statusCounts={statusCounts}
                searchActive={activeView === 'search'}
                onSearch={() => handleSelectView('search')}
                analyticsActive={activeView === 'analytics'}
                onAnalytics={handleOpenAnalytics}
                onBackToVdr={handleBackFromAnalytics}
                onExternalVDR={() => openModal('externalVdr')}
                onAddNew={handleAddNew}
                includeRecording={canUseMeetings}
            />
            <div className={styles.body}>
                <VDRSidebar
                    activeView={activeView}
                    analyticsContext={analyticsContext}
                    onSelectView={handleSelectView}
                    workstreams={workstreams}
                    folders={folders}
                    expandedWorkstreamId={expandedWorkstreamId}
                    selectedWorkstreamId={workstream?.id}
                    selectedFolderId={selectedFolder?.id}
                    onSelectWorkstream={handleSelectWorkstream}
                    onSelectFolder={handleSelectFolder}
                    onAddWorkstream={() => openModal('addWorkstream')}
                    fileCountsByFolder={fileCountsByFolder}
                    fileCountsByWorkstream={fileCountsByWorkstream}
                    initialDocsCount={initialDocs.length}
                    mailCount={projectMails.length}
                    recordingsCount={recordings.length}
                    showMail={canUseMail}
                    showRecordings={canUseMeetings}
                    collapsed={sidebarCollapsed}
                    onToggleCollapse={() => setSidebarCollapsed(c => !c)}
                />
                <div className={styles.mainWithPanel}>
                    <div className={styles.mainColumn}>{renderMain()}</div>
                    {selectedFileForPanel && activeView !== 'mail' && activeView !== 'recordings' && (
                        <FileDetailsPanel
                            file={selectedFileForPanel}
                            workstream={
                                activeView === 'initial'
                                    ? null
                                    : selectedFile?.searchWorkstream?.id
                                      ? selectedFile.searchWorkstream
                                      : workstream
                            }
                            folder={
                                activeView === 'initial'
                                    ? null
                                    : activeView === 'search'
                                      ? selectedFile?.searchFolder || null
                                      : selectedFolder
                            }
                            onClose={() => setSelectedFile(null)}
                            onOpenFile={activeView === 'initial' ? openStoredFile : handleOpenFile}
                            onDownload={activeView === 'initial' ? downloadStoredFile : handleDownloadFile}
                            onOpenRelated={handleOpenRelated}
                            onFileUpdated={patch => {
                                setSelectedFile(current => (current ? { ...current, ...patch } : current));
                                if (workstream?.id) dispatch(GetIRLDoc(workstream.id));
                            }}
                            projectUsersAll={projectUsersAll}
                            indexingFiles={indexingFiles}
                            selectedProject={selectedProject}
                            relatedIrl={selectedFileRelated.irl}
                            relatedObs={selectedFileRelated.obs}
                        />
                    )}
                    {selectedRecording && (
                        <MeetingRecordingPanel
                            recording={selectedRecording}
                            onClose={() => setSelectedRecording(null)}
                            onDelete={handleDeleteRecording}
                            onCreateObservation={handleOpenRecordingObservation}
                            onOpenRelated={item => (item ? handleOpenRelated('related_obs', item) : navigate('/observations'))}
                            relatedObs={observationsForRecording(selectedRecording.id, scopeObservations)}
                        />
                    )}
                    {selectedMail && (
                        <MailDetailPanel
                            mail={selectedMail}
                            onClose={() => setSelectedMail(null)}
                            onCopyLink={mail => copyResourceLink('mail', mail.id)}
                            onRetry={handleRetryMail}
                            onOpenAttachment={attachment => openStoredFile(mailAttachmentFile(attachment))}
                            onDownloadAttachment={attachment => downloadStoredFile(mailAttachmentFile(attachment))}
                            onOpenRelated={(mode, mail) => {
                                const related = relatedForDocuments(mail.documentIds, informationRequests, scopeObservations);
                                const list = mode === 'related_irl' ? related.irl : related.obs;
                                if (list.length === 1) handleOpenRelated(mode, list[0]);
                                else if (list.length > 1) navigate(mode === 'related_irl' ? '/irl' : '/observations');
                            }}
                        />
                    )}
                </div>
            </div>

            <ExternalVDRModal open={modals.externalVdr} onClose={() => closeModal('externalVdr')} />
            <AddWorkstreamModal
                open={modals.addWorkstream || modals.editWorkstream}
                editWorkstream={modals.editWorkstream ? editWorkstreamTarget : null}
                onClose={() => {
                    closeModal('addWorkstream');
                    closeModal('editWorkstream');
                    setEditWorkstreamTarget(null);
                }}
            />
            <CreateFolderModal
                open={modals.createFolder || modals.editFolder}
                editFolder={modals.editFolder ? selectedFolder : null}
                workstream={workstream}
                workstreams={workstreams}
                folders={folders}
                parentFolder={modals.createFolder ? selectedFolder : null}
                onSaved={folder => {
                    if (folder?.id) setSelectedFolder(current => (current?.id === folder.id ? { ...current, ...folder } : current));
                }}
                onClose={() => {
                    closeModal('createFolder');
                    closeModal('editFolder');
                }}
            />
            <CreateFileModal
                open={modals.createFile}
                workstream={workstream}
                folder={selectedFolder}
                onClose={() => closeModal('createFile')}
            />
            <UploadFilesModal
                open={modals.uploadFiles}
                mode="files"
                workstream={workstream}
                folder={selectedFolder}
                onClose={() => closeModal('uploadFiles')}
            />
            <UploadFilesModal
                open={modals.uploadFolder}
                mode="folder"
                workstream={workstream}
                folder={selectedFolder}
                onClose={() => closeModal('uploadFolder')}
            />
            <DeleteConfirmModal
                open={modals.deleteConfirm}
                count={selectedRows.length}
                linked={deleteLinked}
                loading={bulkFilesLoading}
                onClose={() => closeModal('deleteConfirm')}
                onConfirm={handleDeleteSelected}
            />
            <UploadRecordingModal
                open={modals.uploadRecording}
                onClose={() => closeModal('uploadRecording')}
                onUpload={handleUploadRecording}
            />
            <ObservationFormSidebar
                show={observationFormOpen}
                setShow={setObservationFormOpen}
                selectedObservation={null}
                defaultObservation={observationDraft}
                setDefaultData={resetRecordingObservationForm}
                meetingRecordingId={observationRecordingId}
                onCreated={() => dispatch(GetObservationsByScope('all'))}
            />

            {showFileModal && (versionPreview || selectedFile) && (
                <VdrFileViewerModal
                    openModal={showFileModal}
                    setOpenModal={open => {
                        setShowFileModal(open);
                        if (!open) setVersionPreview(null);
                    }}
                    selectedFile={versionPreview || selectedFile}
                    setSelectedFile={setSelectedFile}
                    setSelectedFileIndex={setSelectedFileIndex}
                    selectedFileIndex={selectedFileIndex}
                    workstream={workstream}
                    folderFiles={activeView === 'initial' ? initialDocs : folderFiles}
                    isInitialProjectFile={activeView === 'initial'}
                />
            )}
            <UploadProgressTray />
        </div>
    );
};

export default VDR;
